import ExcelJS from "exceljs";

export const IaReportExcel = ({ reportData, grandTotals, decimalPosition, formValues }) => {
  const downloadExcel = () => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("IA Report");
    sheet.properties.defaultRowHeight = 20;

    // Add title
    const titleRow = sheet.addRow(["IA Report"]);
    titleRow.font = { size: 18, bold: true };
    sheet.mergeCells('A1:AC1');
    titleRow.getCell(1).alignment = { vertical: 'middle', horizontal: 'center' };

   // Add filter information
    const filterInfo = [
      `Branch: ${formValues?.branch?.branchName || ''}`,
      `Location: ${formValues?.location?.name || ''}`,
      `Date Range: ${formValues?.fromDate || ''} to ${formValues?.toDate || ''}`,
      `Raw Materials: ${formValues?.isRawMaterial ? 'TRUE' : 'FALSE'}`
    ];

    // Add each filter info as a separate row
    filterInfo.forEach((info, index) => {
      const row = sheet.addRow([info]);
      row.font = { size: 12, bold: true };
      sheet.mergeCells(`A${index + 2}:AC${index + 2}`);
      row.getCell(1).alignment = { vertical: 'middle', horizontal: 'center' };
    });
    // Add empty row
    sheet.addRow([]);

    // Define headers for IA Report
    const headers = [
      "NO", "Item Code", "Item Name", "Uom", "Conversion", "Rate", 
      "Opening Stock", "Purchase", "Transfer In", 
      "Total Stock-In Qty", "Total Stock-In Value",
      "Purchase Return", "Transfer Out", "Raw Wastage Qty", 
      "SF Wastage", "Finished Wastage Qty", "Total Wastage Qty", "Total Wastage Value",
      "Total Out Qty", "Ideal Cons Qty", "Ideal Cons Value", 
      "Book Closing Qty", "Book Closing Value", "Physical Closing Qty", 
      "ActCons Qty", "ActCons Value", "Variance Qty", "Variance Value"
    ];

    // Add header row
    const headerRow = sheet.addRow(headers);
    
    // Style header row
    const headerFill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF1d1da7" },
    };
    
    const headerFont = {
      size: 12,
      bold: true,
      color: { argb: "FFFFFFFF" },
    };

    headerRow.eachCell((cell) => {
      cell.fill = headerFill;
      cell.font = headerFont;
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FF000000' } },
        left: { style: 'thin', color: { argb: 'FF000000' } },
        bottom: { style: 'thin', color: { argb: 'FF000000' } },
        right: { style: 'thin', color: { argb: 'FF000000' } }
      };
    });

    // Set column widths
    sheet.columns = [
      { key: 'no', width: 5 },
      { key: 'itemCode', width: 12 },
      { key: 'itemName', width: 25 },
      { key: 'uom', width: 8 },
      { key: 'conversion', width: 12 },
      { key: 'rate', width: 10 },
      { key: 'openingStock', width: 15 },
      { key: 'purchase', width: 12 },
      { key: 'transferIn', width: 12 },
      { key: 'totalStockInQty', width: 18 },
      { key: 'totalStockInValue', width: 18 },
      { key: 'purchaseReturn', width: 15 },
      { key: 'transferOut', width: 15 },
      { key: 'rawWastageQty', width: 15 },
      { key: 'sfWastage', width: 12 },
      { key: 'finishedWastageQty', width: 18 },
      { key: 'totalWastageQty', width: 16 },
      { key: 'totalWastageValue', width: 18 },
      { key: 'totalOutQty', width: 15 },
      { key: 'idealConsQty', width: 15 },
      { key: 'idealConsValue', width: 16 },
      { key: 'bookClosingQty', width: 17 },
      { key: 'bookClosingValue', width: 18 },
      { key: 'physicalClosingQty', width: 18 },
      { key: 'actConsQty', width: 15 },
      { key: 'actConsValue', width: 16 },
      { key: 'varianceQty', width: 15 },
      { key: 'varianceValue', width: 16 }
    ];

    // Add data rows grouped by category
    let globalItemCounter = 0;
    reportData?.forEach((group, groupIndex) => {
      // Add category header row
      const categoryRow = sheet.addRow([group.itemGroup]);
      sheet.mergeCells(categoryRow.number, 1, categoryRow.number, 28);
      categoryRow.height = 25;
      categoryRow.getCell(1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE6F4EA' } // Light green
      };
      categoryRow.getCell(1).font = {
        bold: true,
        size: 11,
        color: { argb: 'FF1B5E20' } // Dark green
      };
      categoryRow.getCell(1).alignment = {
        vertical: 'middle',
        horizontal: 'left',
        indent: 1
      };
      categoryRow.getCell(1).border = {
        top: { style: 'thin', color: { argb: 'FFCFE8D5' } },
        bottom: { style: 'thin', color: { argb: 'FFCFE8D5' } }
      };

      // Add items in this category
      group.items?.forEach((item, itemIndex) => {
        globalItemCounter++;
        const row = sheet.addRow({
          no: globalItemCounter,
          itemCode: item.itemNo || '',
          itemName: item.itemName || '',
          uom: item.uom || '',
          conversion: item.conversion || '',
          rate: Number(item.rate || 0).toFixed(decimalPosition),
          openingStock: Number(item.openingStock || 0).toFixed(decimalPosition),
          purchase: Number(item.purchase || 0).toFixed(decimalPosition),
          transferIn: Number(item.transferIn || 0).toFixed(decimalPosition),
          totalStockInQty: Number(item.totalStockInQty || 0).toFixed(decimalPosition),
          totalStockInValue: Number(item.totalStockInValue || 0).toFixed(decimalPosition),
          purchaseReturn: Number(item.purchaseReturn || 0).toFixed(decimalPosition),
          transferOut: Number(item.transferOut || 0).toFixed(decimalPosition),
          rawWastageQty: Number(item.rawWastageQty || 0).toFixed(decimalPosition),
          sfWastage: Number(item.sfWastage || 0).toFixed(decimalPosition),
          finishedWastageQty: Number(item.finishedWastageQty || 0).toFixed(decimalPosition),
          totalWastageQty: Number(item.totalWastageQty || 0).toFixed(decimalPosition),
          totalWastageValue: Number(item.totalWastageValue || 0).toFixed(decimalPosition),
          totalOutQty: Number(item.totalOutQty || 0).toFixed(decimalPosition),
          idealConsQty: Number(item.idealConsQty || 0).toFixed(decimalPosition),
          idealConsValue: Number(item.idealConsValue || 0).toFixed(decimalPosition),
          bookClosingQty: Number(item.bookClosingQty || 0).toFixed(decimalPosition),
          bookClosingValue: Number(item.bookClosingValue || 0).toFixed(decimalPosition),
          physicalClosingQty: Number(item.physicalClosingQty || 0).toFixed(decimalPosition),
          actConsQty: Number(item.actConsQty || 0).toFixed(decimalPosition),
          actConsValue: Number(item.actConsValue || 0).toFixed(decimalPosition),
          varianceQty: Number(item.varianceQty || 0).toFixed(decimalPosition),
          varianceValue: Number(item.varianceValue || 0).toFixed(decimalPosition)
        });

        // Style data rows
        row.eachCell((cell, colNumber) => {
          cell.border = {
            top: { style: 'thin', color: { argb: 'FFD3D3D3' } },
            left: { style: 'thin', color: { argb: 'FFD3D3D3' } },
            bottom: { style: 'thin', color: { argb: 'FFD3D3D3' } },
            right: { style: 'thin', color: { argb: 'FFD3D3D3' } }
          };
          
          // Right align numeric columns (columns 6 onwards)
          if (colNumber > 5) {
            cell.alignment = { horizontal: 'right' };
          }
          
          // Color coding for in/out values
          if ([8,9,10,11].includes(colNumber)) { // Stock in columns (green)
            cell.font = { color: { argb: 'FF008000' } };
          } else if ([12,13,14,15,16,17,18,19].includes(colNumber)) { // Stock out columns (red)
            cell.font = { color: { argb: 'FFFF0000' } };
          }
        });
      });

      // Add category total row
      const totalRow = sheet.addRow([
        '', '', '', '', '', `${group.itemGroup} Total`, '',
        '', '',
        Number(group.groupTotals?.totalStockInQty || 0).toFixed(decimalPosition),
        Number(group.groupTotals?.totalStockInValue || 0).toFixed(decimalPosition),
        '', '', '', '', '',
        Number(group.groupTotals?.totalWastageQty || 0).toFixed(decimalPosition),
        Number(group.groupTotals?.totalWastageValue || 0).toFixed(decimalPosition),
        Number(group.groupTotals?.totalOutQty || 0).toFixed(decimalPosition),
        Number(group.groupTotals?.idealConsQty || 0).toFixed(decimalPosition),
        Number(group.groupTotals?.idealConsValue || 0).toFixed(decimalPosition),
        Number(group.groupTotals?.bookClosingQty || 0).toFixed(decimalPosition),
        Number(group.groupTotals?.bookClosingValue || 0).toFixed(decimalPosition),
        Number(group.groupTotals?.physicalClosingQty || 0).toFixed(decimalPosition),
        Number(group.groupTotals?.actConsQty || 0).toFixed(decimalPosition),
        Number(group.groupTotals?.actConsValue || 0).toFixed(decimalPosition),
        Number(group.groupTotals?.varianceQty || 0).toFixed(decimalPosition),
        Number(group.groupTotals?.varianceValue || 0).toFixed(decimalPosition)
      ]);
      
      totalRow.eachCell((cell, colNumber) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFFEB3B' } // Yellow
        };
        cell.font = { bold: true, size: 10 };
        cell.border = {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } }
        };
        if (colNumber > 5) {
          cell.alignment = { horizontal: 'right' };
        }
      });
    });

    // Add grand totals row
    if (grandTotals && Object.keys(grandTotals).length > 0) {
      const grandTotalRow = sheet.addRow([
        '', '', '', '', '', 'Grand Total', '',
        '', '',
        Number(grandTotals.totalStockInQty || 0).toFixed(decimalPosition),
        Number(grandTotals.totalStockInValue || 0).toFixed(decimalPosition),
        '', '', '', '', '',
        Number(grandTotals.totalWastageQty || 0).toFixed(decimalPosition),
        Number(grandTotals.totalWastageValue || 0).toFixed(decimalPosition),
        Number(grandTotals.totalOutQty || 0).toFixed(decimalPosition),
        Number(grandTotals.idealConsQty || 0).toFixed(decimalPosition),
        Number(grandTotals.idealConsValue || 0).toFixed(decimalPosition),
        Number(grandTotals.bookClosingQty || 0).toFixed(decimalPosition),
        Number(grandTotals.bookClosingValue || 0).toFixed(decimalPosition),
        Number(grandTotals.physicalClosingQty || 0).toFixed(decimalPosition),
        Number(grandTotals.actConsQty || 0).toFixed(decimalPosition),
        Number(grandTotals.actConsValue || 0).toFixed(decimalPosition),
        Number(grandTotals.varianceQty || 0).toFixed(decimalPosition),
        Number(grandTotals.varianceValue || 0).toFixed(decimalPosition)
      ]);

      grandTotalRow.eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF4472C4' } // Blue
        };
        cell.font = { bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
        cell.border = {
          top: { style: 'medium', color: { argb: 'FF000000' } },
          bottom: { style: 'medium', color: { argb: 'FF000000' } }
        };
        if (cell.col > 5) {
          cell.alignment = { horizontal: 'right' };
        }
      });
    }
    // Generate and download the file
    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "IA_Report.xlsx";
      anchor.click();
      window.URL.revokeObjectURL(url);
    });
  };

  return downloadExcel;
};